import '../stylesheets/find_moorings.css';

import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import axios from 'axios';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { getMyAccountData, getPublicAccountInfo } from '../actions/account';
import {
  goBackToMainAccount,
  loginToMasterAccount,
  logoutRequest,
} from '../actions/auth';
import { getMyBoats } from '../actions/boats';
import { getBookingPrice } from '../actions/bookings';
import { getPublicMapOverlays } from '../actions/map_overlays';
import {
  getAllMoorings,
  getBoundsForMarina,
  getMooringEnums,
  getPublicMooringInfo,
} from '../actions/moorings';
import { getPoiById, getPois } from '../actions/pois';
import GetInAppStore from '../assets/get_in_app_store.png';
import GetInPlayStore from '../assets/get_in_play_store.png';
import CenterIcon from '../assets/icons/center.png';
import PIcon from '../assets/icons/p_active.png';
import logoSvg from '../assets/logo-light.svg';
import { requireAuthentication } from '../components/AuthModal';
import BookingModal from '../components/BookingModal';
import ConsentWall from '../components/ConsentWall';
import FilterCheckbox from '../components/FilterCheckbox';
import FilterRange from '../components/FilterRange';
import { GOOGLE_MAPS_LIBRARIES, MooringsMap } from '../components/MooringsMap';
import PoiModal from '../components/PoiModal';
import COLORS from '../constants/colors';
import { openAccountModal } from '../modals/AccountModal';
import { openBoatsModal } from '../modals/BoatsModal';
import { openBookingsModal } from '../modals/BookingsModal';
import { authorizedAccountMatchesSearch } from '../utils/authorizedAccountSearch';
import { formatMooringLBT } from '../utils/mooringDimensions';

const buttonBlockStyle = {
  height: 45,
  borderRadius: 5,
  background: 'rgba(0, 0, 0, 0.6)',
  padding: '5px 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const floatingCardStyle = {
  backgroundColor: COLORS.WHITE_OPAQUE,
  borderRadius: 16,
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)',
  padding: 16,
};

const FILTER_DEFAULTS = {
  showUnavailable: true,
  showPois: true,
  showExcludingMarinaFee: true,
  showBouyPlaces: true,
  show_map_overlays: true,
  length_low: 0,
  length_high: 25,
  beam_low: 0,
  beam_high: 10,
  draft_low: 0,
  draft_high: 10,
  draft_deviation: 0,
  boat_types: [],
};

const FILTER_LIMITS = {
  length: { min: 0, max: 25, step: 0.5 },
  beam: { min: 0, max: 10, step: 0.2 },
  draft: { min: 0, max: 10, step: 0.2 },
  waterLevel: { min: 0, max: 2, step: 0.1 },
};

const AVAILABILITY_COLORS = {
  available: '#6dd8a5',
  unavailable: '#ff6b6b',
};

const CHECKIN_HIDDEN_STATUSES = ['booked'];
const FIND_MOORINGS_MODAL_ROUTES = ['account', 'boats', 'bookings'];
const FIND_MOORINGS_ACCOUNT_TAB_ROUTES = {
  payment_methods: 'payment',
};

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    (typeof window !== 'undefined' ? window.opera : '');
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (
    /iPad|iPhone|iPod/.test(userAgent) &&
    !(typeof window !== 'undefined' && window.MSStream)
  ) {
    return 'ios';
  }
  return 'other';
};

const isMobileDevice = () => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined')
    return false;
  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera || '';
  // Check for mobile user agents
  const isMobileUA =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent,
    );
  // Check for touch capability and small screen
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  // return isMobileUA || (hasTouch && isSmallScreen);
  return isSmallScreen;
};

const DATE_DISPLAY_FORMAT = 'DD.MM.YYYY';

const formatDateRange = (fromDate, toDate) => {
  const fromMoment = moment(fromDate);
  const toMoment = moment(toDate);
  if (!fromMoment.isValid() || !toMoment.isValid()) {
    return '';
  }
  return `${fromMoment.format(DATE_DISPLAY_FORMAT)} - ${toMoment.format(
    DATE_DISPLAY_FORMAT,
  )}`;
};

const getNumericValue = (value) => {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const createBoundsAroundPoint = (latitude, longitude, delta = 0.005) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return {
    min_lat: lat - delta,
    max_lat: lat + delta,
    min_lng: lng - delta,
    max_lng: lng + delta,
  };
};

const prepareAvailabilities = (availabilities = [], fromDate, toDate) => {
  if (!Array.isArray(availabilities) || availabilities.length === 0) {
    return [];
  }

  const fromMoment = moment(fromDate);
  const toMoment = moment(toDate);
  const uniqueAvailabilities = [];

  availabilities.forEach((availability) => {
    if (!availability?.from_date || !availability?.to_date) return;
    const duplicateIndex = uniqueAvailabilities.findIndex(
      (existing) =>
        existing.mooring_id === availability.mooring_id &&
        existing.from_date === availability.from_date &&
        existing.to_date === availability.to_date,
    );
    if (duplicateIndex === -1) {
      uniqueAvailabilities.push({ ...availability });
    }
  });

  if (uniqueAvailabilities.length === 0) {
    return [];
  }

  let filtered = uniqueAvailabilities.filter((availability) => {
    const availabilityFrom = moment(availability.from_date);
    const availabilityTo = moment(availability.to_date);
    if (!availabilityFrom.isValid() || !availabilityTo.isValid()) return false;
    return (
      !availabilityTo.isSameOrBefore(fromMoment) &&
      !availabilityFrom.isSameOrAfter(toMoment)
    );
  });

  if (filtered.length === 0) {
    return [];
  }

  filtered.sort((a, b) =>
    moment(a.from_date).isAfter(moment(b.from_date)) ? 1 : -1,
  );

  const earliest = filtered[0];
  if (earliest && moment(earliest.from_date).isBefore(fromMoment)) {
    earliest.from_date = fromMoment.format('YYYY-MM-DD');
  }

  const latest = filtered[filtered.length - 1];
  if (latest && moment(latest.to_date).isAfter(toMoment)) {
    latest.to_date = toMoment.format('YYYY-MM-DD');
  }

  const today = moment().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  const firstAvailability = filtered[0];
  if (
    firstAvailability &&
    firstAvailability.day_by_day &&
    moment(firstAvailability.from_date).isSame(today, 'day')
  ) {
    const firstToDate = moment(firstAvailability.to_date);
    if (firstToDate.isSame(tomorrow, 'day')) {
      firstAvailability.day_by_day = false;
    } else if (firstToDate.isAfter(tomorrow)) {
      const availabilityToday = {
        ...firstAvailability,
        day_by_day: false,
        to_date: tomorrow.format('YYYY-MM-DD'),
      };
      firstAvailability.from_date = tomorrow.format('YYYY-MM-DD');
      filtered = [availabilityToday, ...filtered];
    }
  }

  return filtered;
};

function FindMooringsContent({ t }) {
  const {
    marina_id: marinaIdParam,
    checkin: checkinParam,
    mooring_id: bookMooringIdParam,
  } = useParams();
  const history = useHistory();
  const location = useLocation();
  const normalizedMarinaParam =
    typeof marinaIdParam === 'string' ? marinaIdParam.trim().toLowerCase() : '';
  const modalRouteType = FIND_MOORINGS_MODAL_ROUTES.includes(
    normalizedMarinaParam,
  )
    ? normalizedMarinaParam
    : null;
  const normalizedModalTabParam =
    typeof checkinParam === 'string' ? checkinParam.trim().toLowerCase() : '';
  const accountModalTab =
    modalRouteType === 'account'
      ? FIND_MOORINGS_ACCOUNT_TAB_ROUTES[normalizedModalTabParam] || null
      : null;
  const hasMarinaParam = Boolean(marinaIdParam && !modalRouteType);
  const hasBookMooringParam = Boolean(bookMooringIdParam);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    libraries: GOOGLE_MAPS_LIBRARIES,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(
    moment().add(1, 'day').format('YYYY-MM-DD'),
  );
  const [mapHeight, setMapHeight] = useState(() => {
    return Math.max(window.innerHeight, 480);
  });
  const [moorings, setMoorings] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [selectedMooring, setSelectedMooring] = useState(null);
  const [selectedAvailabilityIndex, setSelectedAvailabilityIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [priceState, setPriceState] = useState({
    dailyRate: null,
    totalPrice: null,
    loading: false,
    error: false,
  });
  const [locationQuery, setLocationQuery] = useState('');
  const [mapInstance, setMapInstance] = useState(null);
  const [autocompleteInstance, setAutocompleteInstance] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLocationAvailable, setGeoLocationAvailable] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState(() => {
    const stored = localStorage.getItem('show_map_overlays');
    const show_map_overlays =
      stored !== null ? JSON.parse(stored) : FILTER_DEFAULTS.show_map_overlays;
    return { ...FILTER_DEFAULTS, show_map_overlays };
  });
  const [map_overlays, setMapOverlays] = useState([]);
  const [berthNumberFilter, setBerthNumberFilter] = useState('');
  const [marinaActionStep, setMarinaActionStep] = useState('hidden');
  const [isCheckinMode, setIsCheckinMode] = useState(() =>
    Boolean(checkinParam),
  );
  const [showCheckinList, setShowCheckinList] = useState(() =>
    Boolean(hasMarinaParam && checkinParam),
  );
  const [isCheckinListCollapsed, setIsCheckinListCollapsed] = useState(true);
  const [isCheckinChosenFromOverlay, setIsCheckinChosenFromOverlay] =
    useState(false);
  const [checkinListSelectedMooringId, setCheckinListSelectedMooringId] =
    useState(null);
  const [marinaDynamicText, setMarinaDynamicText] = useState('');
  const [pois, setPois] = useState([]);
  const [mooringEnums, setMooringEnums] = useState(null);
  const [selectedPoi, setSelectedPoi] = useState(null);
  const [isPoiModalOpen, setIsPoiModalOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [platform, setPlatform] = useState(() => detectPlatform());
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(localStorage.getItem('AuthToken')),
  );
  const [accountEmail, setAccountEmail] = useState(
    () => localStorage.getItem('email') || '',
  );
  const [, setMyAccountData] = useState(null);
  const [authorizedAccounts, setAuthorizedAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem('AuthorizedAccounts');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [accountSearchText, setAccountSearchText] = useState('');
  const [changeAccountMenuOpen, setChangeAccountMenuOpen] = useState(false);
  const [defaultBoatPricingData, setDefaultBoatPricingData] = useState({
    length: null,
    beam: null,
    draft: null,
    type: null,
  });
  const [resolvedBookMarinaId, setResolvedBookMarinaId] = useState(null);
  const [bookInitialMapView, setBookInitialMapView] = useState(null);
  const [bookMooringReady, setBookMooringReady] = useState(
    () => !hasBookMooringParam,
  );

  const boundsRef = useRef(null);
  const fromDateRef = useRef(fromDate);
  const toDateRef = useRef(toDate);
  const geocoderRef = useRef(null);
  const countryBoundsCacheRef = useRef({});
  const requestIdRef = useRef(0);
  const infoRequestIdRef = useRef(0);
  const poiRequestIdRef = useRef(0);
  const priceRequestIdRef = useRef(0);
  const filterChangeSkipRef = useRef(true);
  const filterDebounceRef = useRef(null);
  const filterRef = useRef(filter);
  const showPoisRef = useRef(filter.showPois);
  const accountMenuRef = useRef(null);
  const autoFocusedMooringRef = useRef(false);
  const marinaOverlayDismissedRef = useRef(false);
  const autoOpenBookingRef = useRef(false);
  const bookMooringInitializedRef = useRef(false);
  const mooringFetchSucceededRef = useRef(false);
  const mooringSearchCancelRef = useRef(null);
  const poiSearchCancelRef = useRef(null);
  const marinaId = hasMarinaParam
    ? String(marinaIdParam)
    : resolvedBookMarinaId
      ? String(resolvedBookMarinaId)
      : null;
  const shouldDisplayFilters = true;
  const shouldDisplayBerthNumberFilter =
    hasMarinaParam || Boolean(resolvedBookMarinaId);
  const hasCheckinParam = Boolean(checkinParam);
  const shouldUseCheckinFlow = hasCheckinParam || isCheckinMode;
  const shouldShowFullFilterInMarina = !hasMarinaParam || !shouldUseCheckinFlow;
  const isMarinaCheckinMode = hasMarinaParam && shouldUseCheckinFlow;

  const mapFilter = useMemo(
    () => ({
      show_berth_numbers: shouldDisplayBerthNumberFilter,
      show_map_overlays: filter.show_map_overlays,
    }),
    [filter.show_map_overlays, shouldDisplayBerthNumberFilter],
  );
  const mooringsMapAdditionalProps = useMemo(
    () =>
      shouldDisplayBerthNumberFilter
        ? { mooringLabelModulo: 0, mooringLabelZoom: 21 }
        : {},
    [shouldDisplayBerthNumberFilter],
  );
  const normalizedBerthNumberFilter = useMemo(
    () => berthNumberFilter.trim().toLowerCase(),
    [berthNumberFilter],
  );
  const shouldFilterByBerthNumber = useMemo(
    () =>
      shouldDisplayBerthNumberFilter && normalizedBerthNumberFilter.length > 0,
    [normalizedBerthNumberFilter, shouldDisplayBerthNumberFilter],
  );
  const displayedMoorings = useMemo(() => {
    let baseMoorings = Array.isArray(moorings) ? moorings : [];
    console.log('shouldUseCheckinFlow', baseMoorings);
    if (shouldUseCheckinFlow) {
      baseMoorings = baseMoorings.filter((mooring) => {
        console.log('mooring', mooring.checkin_allowed);
        return Boolean(mooring?.checkin_allowed);
      });
    }
    if (isMarinaCheckinMode) {
      baseMoorings = baseMoorings.filter((mooring) => {
        const availabilityStatus = String(
          mooring?.availability_status || '',
        ).toLowerCase();
        return !CHECKIN_HIDDEN_STATUSES.includes(availabilityStatus);
      });
    }
    if (!shouldFilterByBerthNumber) {
      return baseMoorings;
    }
    return baseMoorings.filter((mooring) => {
      const mooringNumber = mooring?.berth_number;
      if (mooringNumber == null) return false;
      return String(mooringNumber)
        .toLowerCase()
        .includes(normalizedBerthNumberFilter);
    });
  }, [
    isMarinaCheckinMode,
    moorings,
    normalizedBerthNumberFilter,
    shouldUseCheckinFlow,
    shouldFilterByBerthNumber,
  ]);
  const displayedClusters = useMemo(() => {
    if (shouldFilterByBerthNumber) return [];
    return Array.isArray(clusters) ? clusters : [];
  }, [clusters, shouldFilterByBerthNumber]);
  const displayedMapMoorings = useMemo(() => {
    if (
      !hasMarinaParam ||
      !isCheckinMode ||
      !checkinListSelectedMooringId ||
      !Array.isArray(displayedMoorings)
    ) {
      return displayedMoorings;
    }
    return displayedMoorings.filter(
      (mooring) => mooring?.mooring_id === checkinListSelectedMooringId,
    );
  }, [
    checkinListSelectedMooringId,
    displayedMoorings,
    hasMarinaParam,
    isCheckinMode,
  ]);
  const displayedMapClusters = useMemo(() => {
    if (checkinListSelectedMooringId) return [];
    return displayedClusters;
  }, [checkinListSelectedMooringId, displayedClusters]);

  const mapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: Math.max(mapHeight, 480),
    }),
    [mapHeight],
  );

  const filterPanelCardStyle = useMemo(() => {
    const baseStyle = {
      ...floatingCardStyle,
      alignSelf: 'flex-start',
      width: showFilters ? '100%' : 'auto',
      transition:
        'width 0.3s ease, padding 0.3s ease, background 0.3s ease, box-shadow 0.3s ease',
      overflow: 'hidden',
      padding: showFilters ? 16 : 0,
      borderRadius: showFilters ? floatingCardStyle.borderRadius : 0,
    };

    if (!showFilters) {
      baseStyle.backgroundColor = 'transparent';
      baseStyle.boxShadow = 'none';
    }

    return baseStyle;
  }, [showFilters]);

  const filterPanelContentWrapperStyle = useMemo(() => {
    const maxHeightValue = 'calc(100vh - 360px)';
    return {
      marginTop: showFilters ? 16 : 0,
      borderRadius: 12,
      border: showFilters ? `1px solid ${COLORS.GRAY_OUTLINE}` : 'none',
      background: showFilters ? COLORS.WHITE_OPAQUE : 'transparent',
      opacity: showFilters ? 1 : 0,
      maxHeight: showFilters ? maxHeightValue : 0,
      overflow: 'hidden',
      pointerEvents: showFilters ? 'auto' : 'none',
      transition: showFilters
        ? 'max-height 0.35s ease 0.2s, opacity 0.25s ease 0.2s, margin-top 0.25s ease 0.2s'
        : 'max-height 0.35s ease, opacity 0.2s ease, margin-top 0.2s ease',
    };
  }, [showFilters]);

  const filterPanelContentStyle = useMemo(() => {
    const maxHeightValue = 'calc(100vh - 360px)';
    return {
      padding: showFilters ? 16 : 0,
      display: 'grid',
      gap: showFilters ? 16 : 0,
      maxHeight: showFilters ? maxHeightValue : 0,
      overflowY: showFilters ? 'auto' : 'hidden',
      overflowX: 'hidden',
      transition: showFilters ? 'padding 0.25s ease 0.2s' : 'padding 0.2s ease',
    };
  }, [showFilters]);

  const filterPanelButtonStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      borderRadius: 10,
      cursor: 'pointer',
      fontWeight: 600,
      textTransform: 'uppercase',
      fontSize: 12,
      letterSpacing: 0.4,
      background: showFilters ? 'rgba(0, 48, 102, 0.08)' : COLORS.WHITE_OPAQUE,
      border: showFilters ? 'none' : `1px solid ${COLORS.GRAY_OUTLINE}`,
      color: COLORS.MAIN_BLUE,
      boxShadow: showFilters ? 'none' : '0 4px 12px rgba(0,0,0,0.08)',
      transition:
        'background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease',
    }),
    [showFilters],
  );

  const isCheckinListExpanded = !isCheckinListCollapsed;

  const checkinListContentWrapperStyle = useMemo(
    () => ({
      opacity: isCheckinListExpanded ? 1 : 0,
      maxHeight: isCheckinListExpanded ? 320 : 0,
      overflow: 'hidden',
      pointerEvents: isCheckinListExpanded ? 'auto' : 'none',
      transition: isCheckinListExpanded
        ? 'max-height 0.35s ease 0.2s, opacity 0.25s ease 0.2s'
        : 'max-height 0.35s ease, opacity 0.2s ease',
    }),
    [isCheckinListExpanded],
  );

  const checkinListContentStyle = useMemo(
    () => ({
      display: 'grid',
      gap: isCheckinListExpanded ? 8 : 0,
      paddingTop: isCheckinListExpanded ? 6 : 0,
      transition: isCheckinListExpanded
        ? 'padding-top 0.25s ease 0.2s, gap 0.25s ease 0.2s'
        : 'padding-top 0.2s ease, gap 0.2s ease',
    }),
    [isCheckinListExpanded],
  );

  const checkinListCardStyle = useMemo(() => {
    const baseStyle = {
      ...floatingCardStyle,
      alignSelf: 'flex-start',
      width: isCheckinListExpanded ? '100%' : 'auto',
      transition:
        'width 0.3s ease, padding 0.3s ease, background 0.3s ease, box-shadow 0.3s ease',
      overflow: 'hidden',
      padding: isCheckinListExpanded ? 16 : 0,
      borderRadius: isCheckinListExpanded ? floatingCardStyle.borderRadius : 0,
    };

    if (!isCheckinListExpanded) {
      baseStyle.backgroundColor = 'transparent';
      baseStyle.boxShadow = 'none';
    }

    return baseStyle;
  }, [isCheckinListExpanded]);

  const checkinListToggleButtonStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      borderRadius: 10,
      cursor: 'pointer',
      fontWeight: 600,
      textTransform: 'uppercase',
      fontSize: 12,
      letterSpacing: 0.4,
      background: isCheckinListExpanded
        ? 'rgba(0, 48, 102, 0.08)'
        : COLORS.WHITE_OPAQUE,
      border: isCheckinListExpanded
        ? 'none'
        : `1px solid ${COLORS.GRAY_OUTLINE}`,
      color: COLORS.MAIN_BLUE,
      boxShadow: isCheckinListExpanded ? 'none' : '0 4px 12px rgba(0,0,0,0.08)',
      transition:
        'background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease',
    }),
    [isCheckinListExpanded],
  );

  const boatTypeLabels = useMemo(() => {
    const labels = t('global.boatTypes', { returnObjects: true });
    return Array.isArray(labels) ? labels : [];
  }, [t]);

  const boatTypeOptions = Array.isArray(mooringEnums?.boat_types)
    ? mooringEnums.boat_types
    : [];

  const selectedBoatTypes = Array.isArray(filter.boat_types)
    ? filter.boat_types
    : [];

  const updateMapHeight = useCallback(() => {
    setMapHeight(Math.max(window.innerHeight, 480));
  }, []);

  const refreshMoorings = useCallback(
    (customBounds = null) => {
      const bounds = customBounds || boundsRef.current;
      if (!bounds) return;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const activeFilter = filterRef.current || {};
      const requestFilter = { ...activeFilter };
      if (marinaId) {
        requestFilter.marina_id = marinaId;
      } else if ('marina_id' in requestFilter) {
        delete requestFilter.marina_id;
      }
      if (isMarinaCheckinMode) {
        requestFilter.showAppInvisible = true;
      } else if ('showAppInvisible' in requestFilter) {
        delete requestFilter.showAppInvisible;
      }

      const fromDateValue = fromDateRef.current;
      const toDateValue = toDateRef.current;

      if (mooringSearchCancelRef.current) {
        mooringSearchCancelRef.current.cancel();
      }
      const cancelSource = axios.CancelToken.source();
      mooringSearchCancelRef.current = cancelSource;

      getAllMoorings(
        fromDateValue,
        toDateValue,
        bounds,
        requestFilter,
        (response) => {
          if (requestId !== requestIdRef.current) return;
          const markers = Array.isArray(response?.markers)
            ? response.markers
            : [];
          const clusterItems = Array.isArray(response?.clusters)
            ? response.clusters
            : [];
          mooringFetchSucceededRef.current = true;
          setMoorings(markers);
          setClusters(clusterItems);
        },
        () => {
          if (requestId !== requestIdRef.current) return;
          mooringFetchSucceededRef.current = false;
          setMoorings([]);
          setClusters([]);
        },
        cancelSource.token,
      );
    },
    [isMarinaCheckinMode, marinaId],
  );

  const fetchPois = useCallback((bounds) => {
    if (!showPoisRef.current) {
      setPois([]);
      return;
    }
    if (!bounds) return;
    const requestId = poiRequestIdRef.current + 1;
    poiRequestIdRef.current = requestId;

    if (poiSearchCancelRef.current) {
      poiSearchCancelRef.current.cancel();
    }
    const cancelSource = axios.CancelToken.source();
    poiSearchCancelRef.current = cancelSource;

    getPois(
      bounds,
      (response) => {
        if (poiRequestIdRef.current !== requestId) return;
        const poiItems = Array.isArray(response) ? response : [];
        setPois(poiItems);
      },
      () => {
        if (poiRequestIdRef.current !== requestId) return;
        setPois([]);
      },
      cancelSource.token,
    );
  }, []);

  const handleBoundsChange = useCallback(
    (newBounds) => {
      boundsRef.current = newBounds;
      refreshMoorings(newBounds);
      fetchPois(newBounds);
    },
    [fetchPois, refreshMoorings],
  );

  const ensureGeocoder = useCallback(() => {
    if (geocoderRef.current) {
      return geocoderRef.current;
    }
    if (window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
      return geocoderRef.current;
    }
    return null;
  }, []);

  const normalizeBoundsResponse = useCallback(
    (boundsData) =>
      new Promise((resolve, reject) => {
        if (!boundsData) {
          reject(new Error('No bounds data'));
          return;
        }

        const minLat = Number(boundsData.min_lat);
        const maxLat = Number(boundsData.max_lat);
        const minLng = Number(boundsData.min_lng);
        const maxLng = Number(boundsData.max_lng);
        const numericBounds = {
          min_lat: minLat,
          max_lat: maxLat,
          min_lng: minLng,
          max_lng: maxLng,
        };
        const hasValidBounds = Object.values(numericBounds).every((value) =>
          Number.isFinite(value),
        );

        const countryCodeRaw =
          typeof boundsData.country === 'string'
            ? boundsData.country
            : typeof boundsData.country_code === 'string'
              ? boundsData.country_code
              : '';
        const countryCode = countryCodeRaw.trim().toUpperCase();

        const resolveWithCountry = () => {
          const cachedBounds = countryBoundsCacheRef.current[countryCode];
          if (cachedBounds) {
            resolve(cachedBounds);
            return;
          }

          const geocoder = ensureGeocoder();
          if (!geocoder) {
            if (hasValidBounds) {
              resolve(numericBounds);
              return;
            }
            reject(new Error('Geocoder is not available'));
            return;
          }
          // Use componentRestrictions for more reliable country geocoding
          geocoder.geocode(
            {
              address: countryCode,
              componentRestrictions: { country: countryCode },
            },
            (results, status) => {
              console.log(
                'Geocode results for country',
                countryCode,
                results,
                status,
              );
              if (
                status === 'OK' &&
                Array.isArray(results) &&
                results.length > 0 &&
                results[0]?.geometry
              ) {
                const geometryBounds =
                  results[0].geometry.bounds || results[0].geometry.viewport;
                if (geometryBounds) {
                  const northEast = geometryBounds.getNorthEast();
                  const southWest = geometryBounds.getSouthWest();
                  const normalizedBounds = {
                    min_lat: southWest.lat(),
                    min_lng: southWest.lng(),
                    max_lat: northEast.lat(),
                    max_lng: northEast.lng(),
                  };
                  countryBoundsCacheRef.current[countryCode] = normalizedBounds;
                  resolve(normalizedBounds);
                  return;
                }
              } else {
                // Show whole world if geocoding fails
                const worldBounds = {
                  min_lat: -85,
                  min_lng: -180,
                  max_lat: 85,
                  max_lng: 180,
                };
                resolve(worldBounds);
                return;
              }

              if (hasValidBounds) {
                resolve(numericBounds);
                return;
              }

              reject(
                new Error(`Failed to resolve bounds for country (${status})`),
              );
            },
          );
        };

        if (countryCode) {
          resolveWithCountry();
          return;
        }

        if (hasValidBounds) {
          resolve(numericBounds);
          return;
        }

        reject(new Error('No bounds or country information provided'));
      }),
    [ensureGeocoder],
  );

  const fetchMapBounds = useCallback(
    (onSuccess, onError) => {
      const handleSuccess = (rawBounds) => {
        normalizeBoundsResponse(rawBounds)
          .then((normalizedBounds) => {
            if (normalizedBounds) {
              onSuccess(normalizedBounds);
            } else if (typeof onError === 'function') {
              onError(new Error('Unable to normalize bounds'));
            }
          })
          .catch((error) => {
            console.error('Failed to prepare map bounds', error);
            if (typeof onError === 'function') {
              onError(error);
            }
          });
      };

      const handleError = (error) => {
        if (typeof onError === 'function') {
          onError(error);
        }
      };

      if (marinaId) {
        getBoundsForMarina(handleSuccess, handleError, marinaId);
      } else {
        getBoundsForMarina(handleSuccess, handleError);
      }
    },
    [marinaId, normalizeBoundsResponse],
  );

  const handlePinClick = useCallback(
    (mooring) => {
      if (!mooring?.mooring_id) return;
      setIsPoiModalOpen(false);
      setSelectedPoi(null);
      const requestId = infoRequestIdRef.current + 1;
      infoRequestIdRef.current = requestId;
      setSelectedMooring(null);

      getPublicMooringInfo(
        mooring.mooring_id,
        fromDate,
        toDate,
        (mooringInfo) => {
          if (requestId !== infoRequestIdRef.current) return;
          setSelectedMooring(mooringInfo);
        },
        () => {},
      );

      if (mapInstance) {
        const target = {
          lat: parseFloat(mooring.latitude),
          lng: parseFloat(mooring.longitude),
        };
        mapInstance.panTo(target);
      }
    },
    [fromDate, toDate, mapInstance],
  );

  const handleMapClick = useCallback(() => {
    setSelectedMooring(null);
    setSelectedPoi(null);
    setIsPoiModalOpen(false);
    setCheckinListSelectedMooringId(null);
  }, []);

  const handleMapReady = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const handleCenterOnUser = useCallback(() => {
    if (!geoLocationAvailable || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };
      setUserLocation(coords);
      if (mapInstance) {
        const nextZoom = mapInstance.getZoom();
        const targetZoom = nextZoom && nextZoom > 13 ? nextZoom : 13;
        mapInstance.panTo({
          lat: coords.latitude,
          lng: coords.longitude,
        });
        mapInstance.setZoom(targetZoom);
        window.google?.maps?.event?.addListenerOnce(mapInstance, 'idle', () => {
          const mapBounds = mapInstance.getBounds();
          if (!mapBounds) return;
          const southWest = mapBounds.getSouthWest();
          const northEast = mapBounds.getNorthEast();
          const newBounds = {
            min_lng: southWest.lng(),
            min_lat: southWest.lat(),
            max_lng: northEast.lng(),
            max_lat: northEast.lat(),
          };
          boundsRef.current = newBounds;
          refreshMoorings(newBounds);
        });
      }
    });
  }, [geoLocationAvailable, mapInstance, refreshMoorings]);

  const handlePoiClick = useCallback(
    (poi) => {
      if (!poi) return;
      getPoiById(
        poi.id,
        (poi) => {
          setSelectedPoi(poi);
          setSelectedMooring(null);
          setIsPoiModalOpen(true);
          const lat = Number(poi.latitude);
          const lng = Number(poi.longitude);
          if (mapInstance && Number.isFinite(lat) && Number.isFinite(lng)) {
            mapInstance.panTo({ lat, lng });
            const currentZoom = mapInstance.getZoom();
            if (!currentZoom || currentZoom < 13) {
              mapInstance.setZoom(13);
            }
          }
        },
        () => setSelectedPoi(poi),
      );
    },
    [mapInstance],
  );

  const handlePoiModalClose = useCallback(() => {
    setIsPoiModalOpen(false);
    setSelectedPoi(null);
  }, []);

  const handleAccountToggle = useCallback(() => {
    setAccountMenuOpen((previous) => !previous);
  }, []);

  const refreshAuthorizedAccounts = useCallback(() => {
    try {
      const stored = localStorage.getItem('AuthorizedAccounts');
      setAuthorizedAccounts(stored ? JSON.parse(stored) : []);
      console.log('stored: ', stored);
    } catch {
      setAuthorizedAccounts([]);
    }
  }, []);

  const syncAuthStateFromStorage = useCallback(() => {
    setIsAuthenticated(Boolean(localStorage.getItem('AuthToken')));
    setAccountEmail(localStorage.getItem('email') || '');
    refreshAuthorizedAccounts();
  }, [refreshAuthorizedAccounts]);

  const handleLogout = useCallback(() => {
    setAccountMenuOpen(false);
    setChangeAccountMenuOpen(false);
    logoutRequest(
      () => syncAuthStateFromStorage(),
      () => syncAuthStateFromStorage(),
    );
  }, [syncAuthStateFromStorage]);

  const handleAccountOpen = useCallback(() => {
    setAccountMenuOpen(false);
    setChangeAccountMenuOpen(false);
    openAccountModal();
  }, []);

  const handleBoatsOpen = useCallback(() => {
    setAccountMenuOpen(false);
    setChangeAccountMenuOpen(false);
    openBoatsModal();
  }, []);

  const handleBookingsOpen = useCallback(() => {
    setAccountMenuOpen(false);
    setChangeAccountMenuOpen(false);
    openBookingsModal();
  }, []);

  useEffect(() => {
    if (!modalRouteType) return;

    if (modalRouteType === 'account') {
      let payment_method_status = null;

      try {
        const search_params = new URLSearchParams(location.search || '');
        const raw_status = (search_params.get('ps') || '').trim().toLowerCase();
        if (['s', 'f', 'c'].includes(raw_status)) {
          payment_method_status = raw_status;
        }
      } catch (error) {
        payment_method_status = null;
      }

      openAccountModal({
        initialActiveTab: accountModalTab || undefined,
        paymentMethodStatus: payment_method_status || undefined,
      });
      return;
    }

    if (modalRouteType === 'boats') {
      openBoatsModal();
      return;
    }

    if (modalRouteType === 'bookings') {
      openBookingsModal();
    }
  }, [accountModalTab, location.search, modalRouteType]);


  const handleChangeAccountToggle = useCallback(() => {
    setChangeAccountMenuOpen((previous) => !previous);
  }, []);

  const handleSwitchAccount = useCallback(
    (account) => {
      setAccountMenuOpen(false);
      setChangeAccountMenuOpen(false);
      if (account.email === accountEmail) return;
      loginToMasterAccount(
        account.account_id,
        () => {},
        () => {},
      );
    },
    [accountEmail],
  );

  const handleGoBackToMainAccount = useCallback(() => {
    setAccountMenuOpen(false);
    setChangeAccountMenuOpen(false);
    goBackToMainAccount();
  }, []);

  const filteredAuthorizedAccounts = useMemo(() => {
    if (!Array.isArray(authorizedAccounts) || authorizedAccounts.length === 0)
      return [];
    if (!accountSearchText.trim()) return authorizedAccounts;
    return authorizedAccounts.filter((account) =>
      authorizedAccountMatchesSearch(account, accountSearchText),
    );
  }, [authorizedAccounts, accountSearchText]);

  const pushModalHistoryState = useCallback((modalType) => {
    if (typeof window === 'undefined') return;
    const currentState = window.history.state;
    if (currentState?.modal === modalType) return;
    window.history.pushState({ modal: modalType }, '');
  }, []);

  const refreshAccountData = useCallback(() => {
    getMyAccountData(
      (account) => {
        setMyAccountData(account);
        if (account?.email) {
          // Request succesed and user is logged in
          setIsAuthenticated(true);
          setAccountEmail(account.email);
          console.log('Account data loaded for:', account?.role);
          const normalizedRole = String(account?.role || '').toLowerCase();
          if (normalizedRole !== 'private' && normalizedRole !== 'guest') {
            // Redirect to /home using useHistory (React Router v5)
            history.push('/home');
          }

          getMyBoats(
            (boats) => {
              const defaultBoatId =
                account?.default_boat_id ?? account?.selected_boat_id ?? null;
              const defaultBoat = Array.isArray(boats)
                ? boats.find((boat) => boat.id === defaultBoatId) || boats[0]
                : null;
              const defaultBoatLength = getNumericValue(defaultBoat?.length);
              const defaultBoatBeam = getNumericValue(defaultBoat?.beam);
              const defaultBoatDraft = getNumericValue(defaultBoat?.draft);
              const defaultBoatType = defaultBoat?.type ?? null;

              setDefaultBoatPricingData({
                length: defaultBoatLength,
                beam: defaultBoatBeam,
                draft: defaultBoatDraft,
                type: defaultBoatType,
              });
            },
            () => {
              setDefaultBoatPricingData({
                length: null,
                beam: null,
                draft: null,
                type: null,
              });
            },
          );
        } else {
          setDefaultBoatPricingData({
            length: null,
            beam: null,
            draft: null,
            type: null,
          });
        }
      },
      (err) => {
        console.log('getMyAccountData - err: ', err);
      },
    );
  }, [history]);

  const handleAuthSuccess = useCallback(() => {
    syncAuthStateFromStorage();
    refreshAccountData();
  }, [refreshAccountData, syncAuthStateFromStorage]);

  useEffect(() => {
    const onSessionCleared = () => syncAuthStateFromStorage();
    window.addEventListener('auth-session-cleared', onSessionCleared);
    return () =>
      window.removeEventListener('auth-session-cleared', onSessionCleared);
  }, [syncAuthStateFromStorage]);

  useEffect(() => {
    refreshAccountData();
    refreshAuthorizedAccounts();
  }, [refreshAccountData, refreshAuthorizedAccounts]);

  useEffect(() => {
    getPublicMapOverlays(
      (overlays) => setMapOverlays(Array.isArray(overlays) ? overlays : []),
      () => setMapOverlays([]),
    );
  }, []);

  const handleAutocompleteLoad = useCallback((instance) => {
    setAutocompleteInstance(instance);
  }, []);

  const handlePlaceChanged = useCallback(() => {
    if (!autocompleteInstance) return;
    const place = autocompleteInstance.getPlace();
    if (!place || !place.geometry) return;
    const location = place.geometry.location;
    setLocationQuery(place.formatted_address || place.name || '');
    if (!mapInstance) return;

    const center = {
      lat: location.lat(),
      lng: location.lng(),
    };

    mapInstance.panTo(center);
    const currentZoom = mapInstance.getZoom();
    if (!currentZoom || currentZoom < 13) {
      mapInstance.setZoom(13);
    }

    window.google?.maps?.event?.addListenerOnce(mapInstance, 'idle', () => {
      const mapBounds = mapInstance.getBounds();
      if (!mapBounds) return;
      const southWest = mapBounds.getSouthWest();
      const northEast = mapBounds.getNorthEast();
      const newBounds = {
        min_lng: southWest.lng(),
        min_lat: southWest.lat(),
        max_lng: northEast.lng(),
        max_lat: northEast.lat(),
      };
      boundsRef.current = newBounds;
      refreshMoorings(newBounds);
    });
  }, [autocompleteInstance, mapInstance, refreshMoorings]);

  const handleLocationQueryChange = useCallback((event) => {
    setLocationQuery(event.target.value);
  }, []);

  const toggleFilterVisibility = useCallback(() => {
    setShowFilters((previous) => !previous);
  }, []);

  const handleCheckboxToggle = useCallback((key) => {
    setFilter((previous) => {
      const nextValue = !previous[key];
      if (key === 'show_map_overlays') {
        localStorage.setItem('show_map_overlays', JSON.stringify(nextValue));
      }
      return {
        ...previous,
        [key]: nextValue,
      };
    });
  }, []);

  const handleBerthNumberChange = useCallback((event) => {
    const { value } = event.target;
    setBerthNumberFilter(value);
  }, []);

  const handleCheckinChoice = useCallback(() => {
    setIsCheckinMode(true);
    setIsCheckinChosenFromOverlay(true);
    setShowCheckinList(true);
    setIsCheckinListCollapsed(true);
    setCheckinListSelectedMooringId(null);
    setMarinaActionStep('hidden');
    setFilter((previous) => ({
      ...previous,
      showUnavailable: true,
    }));
  }, []);

  const handleFindAndBookChoice = useCallback(() => {
    setIsCheckinMode(false);
    setIsCheckinChosenFromOverlay(false);
    setShowCheckinList(false);
    setIsCheckinListCollapsed(true);
    setCheckinListSelectedMooringId(null);
    setMarinaActionStep('find_info');
    setFilter((previous) => ({
      ...previous,
      showUnavailable: false,
    }));
  }, []);

  const toggleCheckinListVisibility = useCallback(() => {
    setIsCheckinListCollapsed((previous) => !previous);
  }, []);

  const handleCloseMarinaInfo = useCallback(() => {
    marinaOverlayDismissedRef.current = true;
    setMarinaActionStep('hidden');
  }, []);

  const handleRangeChange = useCallback(
    (lowKey, highKey) => (values) => {
      if (!Array.isArray(values) || values.length < 2) return;
      setFilter((previous) => ({
        ...previous,
        [lowKey]: values[0],
        [highKey]: values[1],
      }));
    },
    [],
  );

  const handleSingleRangeChange = useCallback(
    (key) => (value) => {
      setFilter((previous) => ({
        ...previous,
        [key]: value,
      }));
    },
    [],
  );

  const handleBoatTypeToggle = useCallback((typeIndex, enabled) => {
    setFilter((previous) => {
      const currentTypes = Array.isArray(previous.boat_types)
        ? previous.boat_types
        : [];
      const hasType = currentTypes.includes(typeIndex);

      if (enabled) {
        if (hasType) {
          return previous;
        }
        return {
          ...previous,
          boat_types: [...currentTypes, typeIndex].sort((a, b) => a - b),
        };
      }

      if (!hasType) {
        return previous;
      }

      return {
        ...previous,
        boat_types: currentTypes.filter((value) => value !== typeIndex),
      };
    });
  }, []);

  const handleAvailabilityChange = useCallback((event) => {
    const nextIndex = Number(event.target.value);
    setSelectedAvailabilityIndex(
      Number.isNaN(nextIndex) ? 0 : Math.max(0, nextIndex),
    );
  }, []);

  const handleFromDateChange = useCallback((event) => {
    if (!event.target.value) return;
    const newFrom = moment(event.target.value).format('YYYY-MM-DD');
    setFromDate(newFrom);
    setToDate((previous) => {
      if (!previous) return moment(newFrom).add(1, 'day').format('YYYY-MM-DD');
      if (moment(previous).isSameOrBefore(newFrom)) {
        return moment(newFrom).add(1, 'day').format('YYYY-MM-DD');
      }
      return previous;
    });
  }, []);

  const handleToDateChange = useCallback(
    (event) => {
      if (!event.target.value) return;
      const nextTo = moment(event.target.value).format('YYYY-MM-DD');
      if (moment(nextTo).isSameOrBefore(fromDate)) {
        setToDate(moment(fromDate).add(1, 'day').format('YYYY-MM-DD'));
        return;
      }
      setToDate(nextTo);
    },
    [fromDate],
  );

  const processedAvailabilities = useMemo(
    () =>
      selectedMooring
        ? prepareAvailabilities(
            selectedMooring.availabilities,
            fromDate,
            toDate,
          )
        : [],
    [selectedMooring, fromDate, toDate],
  );

  useEffect(() => {
    setSelectedAvailabilityIndex(0);
  }, [selectedMooring?.mooring_id, fromDate, toDate]);

  useEffect(() => {
    if (
      selectedAvailabilityIndex > 0 &&
      selectedAvailabilityIndex >= processedAvailabilities.length
    ) {
      setSelectedAvailabilityIndex(0);
    }
  }, [processedAvailabilities.length, selectedAvailabilityIndex]);

  const selectedAvailability =
    processedAvailabilities[selectedAvailabilityIndex] || null;

  useEffect(() => {
    if (
      !selectedMooring?.mooring_id ||
      !selectedAvailability?.from_date ||
      !selectedAvailability?.to_date
    ) {
      setPriceState({
        dailyRate: null,
        totalPrice: null,
        loading: false,
        error: false,
      });
      return;
    }

    const requestId = priceRequestIdRef.current + 1;
    priceRequestIdRef.current = requestId;
    setPriceState({
      dailyRate: null,
      totalPrice: null,
      loading: true,
      error: false,
    });

    const normalizedLength = Number(filter.length_low);
    const normalizedBeam = Number(filter.beam_low);
    const boatLength =
      Number.isFinite(normalizedLength) && normalizedLength > 0
        ? normalizedLength
        : defaultBoatPricingData.length;
    const boatBeam =
      Number.isFinite(normalizedBeam) && normalizedBeam > 0
        ? normalizedBeam
        : defaultBoatPricingData.beam;
    const boatType =
      defaultBoatPricingData.type == null ? 0 : defaultBoatPricingData.type;

    getBookingPrice(
      selectedMooring.mooring_id,
      selectedAvailability.from_date,
      selectedAvailability.to_date,
      boatLength,
      boatBeam,
      boatType,
      0,
      0,
      (response) => {
        if (priceRequestIdRef.current !== requestId) return;
        setPriceState({
          dailyRate: getNumericValue(response?.daily_rate),
          totalPrice: getNumericValue(response?.booking_price),
          loading: false,
          error: false,
        });
      },
      () => {
        if (priceRequestIdRef.current !== requestId) return;
        setPriceState({
          dailyRate: null,
          totalPrice: null,
          loading: false,
          error: true,
        });
      },
    );
  }, [
    filter.length_low,
    filter.beam_low,
    defaultBoatPricingData.length,
    defaultBoatPricingData.beam,
    defaultBoatPricingData.type,
    selectedAvailability?.from_date,
    selectedAvailability?.to_date,
    selectedMooring?.mooring_id,
  ]);

  const bookingLinkBase = process.env.REACT_APP_WEB_APP_LINK || '/';
  const lengthLowValue = Number(filter.length_low);
  const beamLowValue = Number(filter.beam_low);
  const draftLowValue = Number(filter.draft_low);
  const hasCustomLength =
    Number.isFinite(lengthLowValue) &&
    lengthLowValue > FILTER_DEFAULTS.length_low;
  const hasCustomBeam =
    Number.isFinite(beamLowValue) && beamLowValue > FILTER_DEFAULTS.beam_low;
  const hasCustomDraft =
    Number.isFinite(draftLowValue) && draftLowValue > FILTER_DEFAULTS.draft_low;
  const shouldUseCheckinBooking = shouldUseCheckinFlow;
  const bookingPath = selectedMooring
    ? `${bookingLinkBase}Booking/${selectedMooring.mooring_id}${shouldUseCheckinBooking ? '/checkin' : ''}/`
    : '';
  const bookingParams = [];
  if (hasCustomLength) {
    bookingParams.push(`l=${filter.length_low}`);
  }
  if (hasCustomBeam) {
    bookingParams.push(`b=${filter.beam_low}`);
  }
  if (hasCustomDraft) {
    bookingParams.push(`d=${filter.draft_low}`);
  }
  if (selectedAvailability?.from_date) {
    bookingParams.push(`from=${selectedAvailability.from_date}`);
  }
  if (selectedAvailability?.to_date) {
    bookingParams.push(`to=${selectedAvailability.to_date}`);
  }
  const bookingLink = bookingPath
    ? `${bookingPath}${
        bookingParams.length ? `?${bookingParams.join('&')}` : ''
      }`
    : '';
  const canBookUnavailableMooringInCheckin =
    hasMarinaParam && isCheckinMode && isCheckinChosenFromOverlay;

  const currencySymbol = selectedMooring?.payment_currency || '';
  const mooringState = selectedMooring?.availability_status || 'unavailable';
  const unavailableStates = ['unavailable', 'not_released'];
  const isUnavailableState = unavailableStates.includes(mooringState);
  const availabilityColor = isUnavailableState
    ? AVAILABILITY_COLORS.unavailable
    : AVAILABILITY_COLORS.available;
  const fallbackStateLabels = {
    available: 'Available',
    unavailable: 'Unavailable',
    available_day_by_day: 'Available day by day',
    not_released: 'Not released',
  };
  const availabilityLabel = t(
    `FindMoorings.${mooringState}`,
    fallbackStateLabels[mooringState] || mooringState,
  );
  const excludingTouristTaxes =
    Number(selectedMooring?.tourist_tax_adult) !== 0;
  const requiresConfirmation = Boolean(
    selectedMooring?.needs_booking_confirmation,
  );
  const selectedDateRangeLabel = selectedAvailability
    ? formatDateRange(
        selectedAvailability.from_date,
        selectedAvailability.to_date,
      )
    : formatDateRange(fromDate, toDate);
  const hasAvailabilities = processedAvailabilities.length > 0;
  const showAvailabilitySelector = processedAvailabilities.length > 1;
  const isDayByDayAvailability = Boolean(selectedAvailability?.day_by_day);
  const canBypassDayByDayRestriction = Boolean(selectedMooring?.own);
  const showDayByDayNotice =
    isDayByDayAvailability && !canBypassDayByDayRestriction;
  const canBookAvailability = canBookUnavailableMooringInCheckin
    ? Boolean(bookingLink)
    : Boolean(selectedAvailability) &&
      !isUnavailableState &&
      (canBypassDayByDayRestriction || !isDayByDayAvailability) &&
      Boolean(bookingLink);
  const handleBookingModalOpen = useCallback(() => {
    if (!canBookAvailability) return;
    pushModalHistoryState('booking');
    if (isMobileDevice()) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    setIsBookingModalOpen(true);
  }, [canBookAvailability, pushModalHistoryState]);
  const handleBookingModalClose = useCallback((shouldUpdateHistory = true) => {
    setIsBookingModalOpen(false);
    if (shouldUpdateHistory && window.history.state?.modal === 'booking') {
      window.history.back();
    }
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      if (isBookingModalOpen) {
        handleBookingModalClose(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleBookingModalClose, isBookingModalOpen]);
  const availableDatesLabel = t(
    'FindMoorings.availableDates',
    'Available dates',
  );
  const dayByDayLabel = t('FindMoorings.dayByDay', 'Day by day');
  const noAvailabilityMessage = t(
    'FindMoorings.noAvailabilityForRange',
    'No availability for the selected dates',
  );
  const dateRangeText =
    selectedDateRangeLabel || t('FindMoorings.selectDates', 'Select dates');
  const priceLoading = priceState.loading;
  const priceError = priceState.error;
  const dailyPriceText =
    priceState.dailyRate != null
      ? `${currencySymbol} ${priceState.dailyRate.toFixed(2)}`
      : priceLoading
        ? t('global.loading')
        : priceError
          ? '--'
          : t('FindMoorings.onNextScreen');
  let totalPriceText = '';
  if (priceState.totalPrice != null) {
    totalPriceText = `${currencySymbol} ${priceState.totalPrice.toFixed(2)}`;
  } else if (priceLoading) {
    totalPriceText = t('global.loading');
  } else if (priceError) {
    totalPriceText = t('FindMoorings.priceUnavailable', 'Price unavailable');
  } else {
    totalPriceText = t('FindMoorings.byBoatSize', 'Price based on boat size');
  }

  const formatDimension = (value) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric.toFixed(2);
    }
    return '0.00';
  };
  const bookingFromDate = selectedAvailability?.from_date || fromDate;
  const bookingToDate = selectedAvailability?.to_date || toDate;
  const bookingModalProps = {
    mooringId: selectedMooring?.mooring_id,
    from: bookingFromDate,
    to: bookingToDate,
    boatLength: hasCustomLength ? filter.length_low : null,
    boatBeam: hasCustomBeam ? filter.beam_low : null,
    boatDraft: hasCustomDraft ? filter.draft_low : null,
    checkin: shouldUseCheckinBooking,
    onAuthSuccess: handleAuthSuccess,
  };
  const findAndBookInfoMessage = `${t(
    'FindMoorings.findAndBookInfo',
    'Click on the pin for berth information and immediate reservation. Please reserve the berth before final mooring! For reservations at a later date or for several days, please download the app to your mobile device (via buttons at the bottom left).',
  )}${marinaDynamicText ? ` ${marinaDynamicText}` : ''}`;

  useEffect(() => {
    updateMapHeight();
    window.addEventListener('resize', updateMapHeight);
    return () => window.removeEventListener('resize', updateMapHeight);
  }, [updateMapHeight]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    if (!isLoaded) return;
    ensureGeocoder();
  }, [ensureGeocoder, isLoaded]);

  useEffect(() => {
    fromDateRef.current = fromDate;
  }, [fromDate]);

  useEffect(() => {
    toDateRef.current = toDate;
  }, [toDate]);

  useEffect(() => {
    showPoisRef.current = filter.showPois;
  }, [filter.showPois]);

  useEffect(() => {
    if (!accountMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target)
      ) {
        setAccountMenuOpen(false);
        setChangeAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (accountMenuOpen) return;
    setChangeAccountMenuOpen(false);
    setAccountSearchText('');
  }, [accountMenuOpen]);

  useEffect(() => {
    setGeoLocationAvailable('geolocation' in navigator);
    setPlatform(detectPlatform());
  }, []);

  useEffect(() => {
    getMooringEnums(
      (enumsResponse) => {
        setMooringEnums(enumsResponse);
        if (
          Array.isArray(enumsResponse?.boat_types) &&
          enumsResponse.boat_types.length > 0
        ) {
          setFilter((previousFilter) => {
            if (
              Array.isArray(previousFilter.boat_types) &&
              previousFilter.boat_types.length > 0
            ) {
              return previousFilter;
            }
            return {
              ...previousFilter,
              boat_types: enumsResponse.boat_types.map((_, index) => index),
            };
          });
        }
      },
      () => {},
    );
  }, []);

  useEffect(() => {
    if (filterChangeSkipRef.current) {
      filterChangeSkipRef.current = false;
      return;
    }

    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    filterDebounceRef.current = setTimeout(() => {
      if (boundsRef.current) {
        refreshMoorings(boundsRef.current);
      }
    }, 600);

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = null;
      }
    };
  }, [filter, refreshMoorings]);

  useEffect(() => {
    if (!filter.showPois) {
      setPois([]);
      setSelectedPoi(null);
      setIsPoiModalOpen(false);
      return;
    }
    if (boundsRef.current) {
      fetchPois(boundsRef.current);
    }
  }, [fetchPois, filter.showPois]);

  useEffect(() => {
    const nav = document.getElementById('navbar');
    if (!nav) return undefined;

    const previousDisplay = nav.style.display;
    nav.style.display = 'none';

    return () => {
      nav.style.display = previousDisplay;
    };
  }, []);

  useEffect(() => {
    autoFocusedMooringRef.current = false;
    marinaOverlayDismissedRef.current = false;
    mooringFetchSucceededRef.current = false;
  }, [marinaId]);

  useEffect(() => {
    mooringFetchSucceededRef.current = false;
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!hasMarinaParam) return;
    setFilter((previous) => {
      const nextShowUnavailable = shouldUseCheckinFlow;
      if (previous.showUnavailable === nextShowUnavailable) {
        return previous;
      }
      return {
        ...previous,
        showUnavailable: nextShowUnavailable,
      };
    });
  }, [hasMarinaParam, shouldUseCheckinFlow]);

  useEffect(() => {
    if (!hasMarinaParam) {
      setMarinaActionStep('hidden');
      setIsCheckinMode(false);
      setIsCheckinChosenFromOverlay(false);
      setShowCheckinList(false);
      setIsCheckinListCollapsed(true);
      setCheckinListSelectedMooringId(null);
      setMarinaDynamicText('');
      return;
    }
    setMarinaDynamicText('');
    if (hasCheckinParam) {
      setMarinaActionStep('hidden');
      setIsCheckinMode(true);
      setIsCheckinChosenFromOverlay(false);
      setShowCheckinList(true);
      setIsCheckinListCollapsed(true);
      setCheckinListSelectedMooringId(null);
      return;
    }
    // marinaActionStep will be set by getPublicAccountInfo based on allow_booking_unreleased
    setIsCheckinMode(false);
    setIsCheckinChosenFromOverlay(false);
    setShowCheckinList(false);
    setIsCheckinListCollapsed(true);
    setCheckinListSelectedMooringId(null);
  }, [hasCheckinParam, hasMarinaParam, marinaId]);

  useEffect(() => {
    if (!hasMarinaParam || !marinaId) {
      return;
    }
    getPublicAccountInfo(
      marinaId,
      (account) => {
        const nextText =
          typeof account?.bpms_comment === 'string'
            ? account.bpms_comment.trim()
            : '';
        setMarinaDynamicText(nextText);
        if (!hasCheckinParam) {
          setMarinaActionStep(
            account?.allow_booking_unreleased ? 'choice' : 'find_info',
          );
        }
      },
      () => {
        setMarinaDynamicText('');
        if (!hasCheckinParam) {
          setMarinaActionStep('find_info');
        }
      },
    );
  }, [hasMarinaParam, marinaId, hasCheckinParam]);

  useEffect(() => {
    if (!hasBookMooringParam) {
      bookMooringInitializedRef.current = false;
      autoOpenBookingRef.current = false;
      setResolvedBookMarinaId(null);
      setBookInitialMapView(null);
      setBookMooringReady(true);
      return;
    }

    bookMooringInitializedRef.current = false;
    autoOpenBookingRef.current = false;
    setResolvedBookMarinaId(null);
    setBookInitialMapView(null);
    setBookMooringReady(false);
    setSelectedMooring(null);
    setIsBookingModalOpen(false);
  }, [bookMooringIdParam, hasBookMooringParam]);

  useEffect(() => {
    if (!hasBookMooringParam || !bookMooringIdParam || !isLoaded) return;

    const requestId = infoRequestIdRef.current + 1;
    infoRequestIdRef.current = requestId;

    getPublicMooringInfo(
      bookMooringIdParam,
      fromDate,
      toDate,
      (mooringInfo) => {
        if (requestId !== infoRequestIdRef.current) return;
        bookMooringInitializedRef.current = true;

        const lat = parseFloat(mooringInfo?.latitude);
        const lng = parseFloat(mooringInfo?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setBookInitialMapView({ lat, lng, zoom: 18 });
        }

        setSelectedMooring(mooringInfo);
        setBookMooringReady(true);

        const marinaAccountId =
          mooringInfo?.landlord_account_id ?? mooringInfo?.supervising_marina;
        if (marinaAccountId) {
          setResolvedBookMarinaId(String(marinaAccountId));
          return;
        }

        const pointBounds = createBoundsAroundPoint(lat, lng);
        if (pointBounds) {
          boundsRef.current = pointBounds;
          refreshMoorings(pointBounds);
          fetchPois(pointBounds);
        }
      },
      () => {
        if (requestId !== infoRequestIdRef.current) return;
        bookMooringInitializedRef.current = true;
        setBookMooringReady(true);
      },
    );
  }, [
    bookMooringIdParam,
    fetchPois,
    fromDate,
    hasBookMooringParam,
    isLoaded,
    refreshMoorings,
    toDate,
  ]);

  useEffect(() => {
    if (!hasBookMooringParam || autoOpenBookingRef.current) return;
    if (
      !selectedMooring?.mooring_id ||
      String(selectedMooring.mooring_id) !== String(bookMooringIdParam)
    ) {
      return;
    }

    autoOpenBookingRef.current = true;
    pushModalHistoryState('booking');
    if (isMobileDevice()) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    setIsBookingModalOpen(true);
  }, [
    bookMooringIdParam,
    hasBookMooringParam,
    pushModalHistoryState,
    selectedMooring,
  ]);

  useEffect(() => {
    if (!isLoaded) return;
    if (hasBookMooringParam && !bookMooringInitializedRef.current) return;
    if (hasBookMooringParam && !marinaId && boundsRef.current) return;

    fetchMapBounds(
      (initialBounds) => {
        boundsRef.current = initialBounds;
        refreshMoorings(initialBounds);
        fetchPois(initialBounds);
      },
      () => {},
    );
  }, [
    fetchMapBounds,
    fetchPois,
    hasBookMooringParam,
    isLoaded,
    marinaId,
    refreshMoorings,
  ]);

  useEffect(() => {
    if (boundsRef.current) {
      refreshMoorings(boundsRef.current);
    }
  }, [fromDate, toDate, refreshMoorings]);

  useEffect(() => {
    const pollIfVisible = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (boundsRef.current) {
        refreshMoorings(boundsRef.current);
      }
    };

    const intervalId = setInterval(pollIfVisible, 60000);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        pollIfVisible();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (mooringSearchCancelRef.current) {
        mooringSearchCancelRef.current.cancel();
      }
      if (poiSearchCancelRef.current) {
        poiSearchCancelRef.current.cancel();
      }
    };
  }, [refreshMoorings]);

  useEffect(() => {
    if (!shouldFilterByBerthNumber || !selectedMooring) return;
    const selectedNumber = selectedMooring.berth_number;
    if (
      selectedNumber == null ||
      !String(selectedNumber)
        .toLowerCase()
        .includes(normalizedBerthNumberFilter)
    ) {
      setSelectedMooring(null);
    }
  }, [normalizedBerthNumberFilter, selectedMooring, shouldFilterByBerthNumber]);

  useEffect(() => {
    if (!hasMarinaParam || autoFocusedMooringRef.current) return;
    // Don't auto-open the booking popup while the user is still deciding
    // (e.g. choosing "Check-in and pay" from the marina overlay after a QR scan)
    // or while in the check-in flow. Only auto-select the single mooring once the
    // user has explicitly committed to the find-and-book flow by dismissing the
    // overlay.
    if (shouldUseCheckinFlow || !marinaOverlayDismissedRef.current) return;
    if (marinaActionStep !== 'hidden') return;
    if (!Array.isArray(moorings) || moorings.length !== 1) return;
    autoFocusedMooringRef.current = true;
    handlePinClick(moorings[0]);
  }, [
    handlePinClick,
    hasMarinaParam,
    marinaActionStep,
    moorings,
    shouldUseCheckinFlow,
  ]);

  if (!isLoaded || (hasBookMooringParam && !bookMooringReady)) {
    return (
      <div
        style={{
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: COLORS.MAIN_BLUE,
        }}>
        {t('global.loading')}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: Math.max(mapHeight, 480),
      }}>
      <BookingModal
        show={isBookingModalOpen}
        onHide={handleBookingModalClose}
        {...bookingModalProps}
      />
      <MooringsMap
        allMoorings={displayedMapMoorings}
        clusters={displayedMapClusters}
        filter={mapFilter}
        forceGreenPins={shouldUseCheckinFlow}
        map_overlays={map_overlays}
        containerStyle={mapContainerStyle}
        center={
          bookInitialMapView
            ? { lat: bookInitialMapView.lat, lng: bookInitialMapView.lng }
            : undefined
        }
        zoom={bookInitialMapView?.zoom}
        mapOptions={{
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        }}
        onPinClick={handlePinClick}
        onClick={handleMapClick}
        setBounds={handleBoundsChange}
        onMapReady={handleMapReady}
        fetchBoundsForMarina={fetchMapBounds}
        userLocation={userLocation}
        pois={filter.showPois ? pois : []}
        onPoiClick={handlePoiClick}
        {...mooringsMapAdditionalProps}
      />
      {hasMarinaParam && marinaActionStep !== 'hidden' && (
        <div className='find-moorings-marina-overlay'>
          <div className='find-moorings-marina-card'>
            {marinaActionStep === 'choice' ? (
              <>
                <div className='find-moorings-marina-title'>
                  {t(
                    'FindMoorings.marinaActionTitle',
                    'What do you want to do?',
                  )}
                </div>
                <div className='find-moorings-marina-actions'>
                  <button
                    type='button'
                    className='find-moorings-marina-btn find-moorings-marina-btn-primary'
                    onClick={handleCheckinChoice}>
                    {t(
                      'FindMoorings.marinaActionCheckin',
                      'Check-in and pay (berth already found)',
                    )}
                  </button>
                  <button
                    type='button'
                    className='find-moorings-marina-btn find-moorings-marina-btn-secondary'
                    onClick={handleFindAndBookChoice}>
                    {t(
                      'FindMoorings.marinaActionFindAndBook',
                      'Find mooring and book',
                    )}
                  </button>
                </div>
              </>
            ) : marinaActionStep === 'find_info' ? (
              <>
                <div className='find-moorings-marina-message'>
                  {mooringFetchSucceededRef.current &&
                  Array.isArray(displayedMoorings) &&
                  displayedMoorings.length === 0
                    ? t(
                        'FindMoorings.noMooringAvailable',
                        'No mooring is available for the selected dates.',
                      )
                    : findAndBookInfoMessage}
                </div>
                <div className='find-moorings-marina-actions'>
                  <button
                    type='button'
                    className='find-moorings-marina-btn find-moorings-marina-btn-primary'
                    onClick={handleCloseMarinaInfo}>
                    {t('global.ok', 'Ok')}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 6,
        }}
        ref={accountMenuRef}>
        {isAuthenticated ? (
          <div style={{ position: 'relative' }}>
            <button
              type='button'
              onClick={handleAccountToggle}
              style={{
                ...buttonBlockStyle,
                padding: '5px 16px',
                color: COLORS.MAIN_WHITE,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: 'none',
                cursor: 'pointer',
                textTransform: 'none',
                marginTop: isMobileDevice() ? -8 : 0,
              }}>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                style={{ color: COLORS.MAIN_WHITE }}>
                <circle
                  cx='12'
                  cy='8'
                  r='4'
                  stroke='currentColor'
                  strokeWidth='1.6'
                />
                <path
                  d='M18.5 19c0-3.037-2.91-5.5-6.5-5.5s-6.5 2.463-6.5 5.5'
                  stroke='currentColor'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                />
              </svg>
              <span
                style={{
                  maxWidth: 160,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {accountEmail || t('Navbar.account')}
              </span>
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                style={{
                  transform: accountMenuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                  color: COLORS.MAIN_WHITE,
                }}>
                <path
                  d='M6 10l6 6 6-6'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
            {accountMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  background: '#0d0d0d',
                  borderRadius: 10,
                  padding: '10px 12px',
                  boxShadow: '0 10px 24px rgba(0, 0, 0, 0.35)',
                  minWidth: 220,
                  maxWidth: 300,
                  display: 'grid',
                  gap: 4,
                }}>
                {localStorage.getItem('originalAuthToken') && (
                  <button
                    type='button'
                    onClick={handleGoBackToMainAccount}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: 'none',
                      color: '#6dd8a5',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 13,
                      width: '100%',
                      textAlign: 'left',
                    }}>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 12H5M5 12l6-6M5 12l6 6'
                        stroke='currentColor'
                        strokeWidth='1.6'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    {localStorage.getItem('originalEmail') ||
                      t('Navbar.account')}
                  </button>
                )}
                {authorizedAccounts.length > 0 && (
                  <>
                    <button
                      type='button'
                      onClick={handleChangeAccountToggle}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: changeAccountMenuOpen
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'transparent',
                        border: 'none',
                        color: COLORS.MAIN_WHITE,
                        fontWeight: 600,
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'left',
                      }}>
                      <span>{t('Navbar.changeAccount', 'Change account')}</span>
                      <svg
                        width='12'
                        height='12'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        style={{
                          transform: changeAccountMenuOpen
                            ? 'rotate(180deg)'
                            : 'none',
                          transition: 'transform 0.2s ease',
                          color: COLORS.MAIN_WHITE,
                          flexShrink: 0,
                        }}>
                        <path
                          d='M6 10l6 6 6-6'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </button>
                    {changeAccountMenuOpen && (
                      <div
                        style={{
                          display: 'grid',
                          gap: 6,
                          padding: 8,
                          borderRadius: 8,
                          background: 'rgba(255, 255, 255, 0.06)',
                        }}>
                        {authorizedAccounts.length > 3 && (
                          <input
                            type='text'
                            value={accountSearchText}
                            onChange={(e) =>
                              setAccountSearchText(e.target.value)
                            }
                            placeholder={t('global.search', 'Search')}
                            style={{
                              width: '100%',
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              background: 'rgba(0, 0, 0, 0.18)',
                              color: COLORS.MAIN_WHITE,
                              fontSize: 13,
                              outline: 'none',
                            }}
                          />
                        )}
                        <div
                          style={{
                            maxHeight: 200,
                            overflowY: 'auto',
                            display: 'grid',
                            gap: 2,
                          }}>
                          {filteredAuthorizedAccounts.map((account) => (
                            <button
                              key={account.account_id}
                              type='button'
                              onClick={() => handleSwitchAccount(account)}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '7px 10px',
                                borderRadius: 6,
                                background:
                                  account.email === accountEmail
                                    ? 'rgba(255, 255, 255, 0.12)'
                                    : 'transparent',
                                border: 'none',
                                color: COLORS.MAIN_WHITE,
                                fontSize: 13,
                                cursor: 'pointer',
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                              {account.email}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <button
                  type='button'
                  onClick={handleAccountOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: COLORS.MAIN_WHITE,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ color: COLORS.MAIN_WHITE }}>
                    <circle
                      cx='12'
                      cy='8'
                      r='4'
                      stroke='currentColor'
                      strokeWidth='1.6'
                    />
                    <path
                      d='M18.5 19c0-3.037-2.91-5.5-6.5-5.5s-6.5 2.463-6.5 5.5'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                  </svg>
                  My account
                </button>
                <button
                  type='button'
                  onClick={handleBoatsOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: COLORS.MAIN_WHITE,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 32 32'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ color: COLORS.MAIN_WHITE }}>
                    <path
                      d='M20.85,18.91C20.8,15,21,8.68,13,2c0,0,1,12-10,16a25.36,25.36,0,0,0,13,4.6S20.89,22.6,20.85,18.91Z'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M1,23v5s12.25,3.69,21,1c7.76-2.39,9-11,9-11C21,23,18,23,1,23Z'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M20.85,18.91C21,15.18,21,8.68,13,2c0,0,1,12-10,16a25.36,25.36,0,0,0,13,4.6'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <polyline
                      points='13 2 6 2 9 4.5 6 7 12 7'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  My boats
                </button>
                <button
                  type='button'
                  onClick={handleBookingsOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: COLORS.MAIN_WHITE,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ color: COLORS.MAIN_WHITE }}>
                    <path
                      d='M7 7h10'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                    <path
                      d='M7 11h10'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                    <path
                      d='M7 15h6'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                    <rect
                      x='4'
                      y='3'
                      width='16'
                      height='18'
                      rx='2'
                      stroke='currentColor'
                      strokeWidth='1.6'
                    />
                  </svg>
                  My bookings
                </button>
                <button
                  type='button'
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: COLORS.MAIN_WHITE,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{ color: COLORS.MAIN_WHITE }}>
                    <path
                      d='M10 17v1a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M14 16l4-4-4-4'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M8 12h10'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  {t('Navbar.logout')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type='button'
            onClick={() =>
              requireAuthentication({ onAuthSuccess: handleAuthSuccess })
            }
            style={{
              ...buttonBlockStyle,
              padding: '5px 16px',
              color: COLORS.MAIN_WHITE,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              minWidth: 110,
              border: 'none',
              background: buttonBlockStyle.background,
            }}>
            {t('Navbar.login')}
          </button>
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          width: '90%',
          maxWidth: 400,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
        className='find-moorings-filters'>
        <div style={{ ...floatingCardStyle }}>
          {!hasMarinaParam && (
            <div style={{ marginBottom: 12 }}>
              <Autocomplete
                onLoad={handleAutocompleteLoad}
                onPlaceChanged={handlePlaceChanged}
                options={{
                  fields: ['geometry', 'formatted_address', 'name'],
                  types: ['establishment', 'geocode'],
                }}>
                <Form.Control
                  value={locationQuery}
                  onChange={handleLocationQueryChange}
                  placeholder={t('BpmsMooringMap.desiredLocation')}
                  autoComplete='off'
                  style={{
                    borderRadius: 10,
                    border: `1px solid ${COLORS.GRAY_OUTLINE}`,
                    paddingLeft: 12,
                    height: 40,
                  }}
                />
              </Autocomplete>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'nowrap',
              alignItems: 'flex-end',
              width: '100%',
            }}>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: COLORS.MAIN_GRAY,
                  marginBottom: 4,
                }}>
                {t('global.from')}
              </div>
              <Form.Control
                type='date'
                value={fromDate}
                onChange={handleFromDateChange}
                min={moment().subtract(1, 'day').format('YYYY-MM-DD')}
                style={{
                  borderRadius: 10,
                  border: `1px solid ${COLORS.GRAY_OUTLINE}`,
                  paddingLeft: 12,
                  height: 40,
                }}
              />
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: COLORS.MAIN_GRAY,
                  marginBottom: 4,
                }}>
                {t('global.to')}
              </div>
              <Form.Control
                type='date'
                value={toDate}
                onChange={handleToDateChange}
                min={moment(fromDate).add(1, 'day').format('YYYY-MM-DD')}
                style={{
                  borderRadius: 10,
                  border: `1px solid ${COLORS.GRAY_OUTLINE}`,
                  paddingLeft: 12,
                  height: 40,
                }}
              />
            </div>
          </div>
          {shouldDisplayBerthNumberFilter && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: COLORS.MAIN_GRAY,
                  marginBottom: 4,
                }}>
                {t('FindMoorings.berthNumberFilterLabel', 'Berth number')}
              </div>
              <Form.Control
                type='text'
                value={berthNumberFilter}
                placeholder={t(
                  'FindMoorings.berthNumberFilterPlaceholder',
                  'Search berth number',
                )}
                onChange={handleBerthNumberChange}
                style={{
                  borderRadius: 10,
                  border: `1px solid ${COLORS.GRAY_OUTLINE}`,
                  paddingLeft: 12,
                  height: 40,
                }}
              />
            </div>
          )}
        </div>
        {shouldDisplayFilters && (
          <div style={filterPanelCardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: 'fit-content',
                gap: 8,
                flexWrap: 'wrap',
              }}>
              <div
                onClick={toggleFilterVisibility}
                style={filterPanelButtonStyle}>
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M2 2H14L9.5 7.25V12L6.5 14V7.25L2 2Z'
                    stroke={COLORS.MAIN_BLUE}
                    strokeWidth='1.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    fill='none'
                  />
                </svg>
                <span>
                  {showFilters
                    ? t('global.hide', 'Hide')
                    : t('global.filter', 'Filter')}
                </span>
                <span style={{ fontSize: 16 }}>{showFilters ? '−' : '+'}</span>
              </div>
              {hasMarinaParam && showCheckinList && isCheckinListCollapsed && (
                <div
                  onClick={toggleCheckinListVisibility}
                  style={checkinListToggleButtonStyle}>
                  <span>{t('FindMoorings.listView', 'List view')}</span>
                  <span style={{ fontSize: 16 }}>
                    {isCheckinListExpanded ? '−' : '+'}
                  </span>
                </div>
              )}
            </div>

            <div style={filterPanelContentWrapperStyle}>
              <div
                className='find-moorings-panel'
                style={filterPanelContentStyle}>
                {shouldShowFullFilterInMarina && (
                  <div
                    style={{
                      display: 'grid',
                      gap: 12,
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                    }}>
                    <FilterCheckbox
                      label={t(
                        'FindMoorings.showUnavailable',
                        'Show unavailable',
                      )}
                      checked={filter.showUnavailable}
                      onChange={() => handleCheckboxToggle('showUnavailable')}
                    />
                    <FilterCheckbox
                      label={t(
                        'FindMoorings.showPois',
                        'Show Points of Interest',
                      )}
                      checked={filter.showPois}
                      onChange={() => handleCheckboxToggle('showPois')}
                    />
                    <FilterCheckbox
                      label={t(
                        'FindMoorings.showExcludingMarinaFee',
                        'Show excl. marina fee',
                      )}
                      checked={filter.showExcludingMarinaFee}
                      onChange={() =>
                        handleCheckboxToggle('showExcludingMarinaFee')
                      }
                    />
                    <FilterCheckbox
                      label={t(
                        'FindMoorings.showBouyPlaces',
                        'Show buoy places',
                      )}
                      checked={filter.showBouyPlaces}
                      onChange={() => handleCheckboxToggle('showBouyPlaces')}
                    />
                  </div>
                )}
                <div style={{ display: 'grid', gap: 16 }}>
                  <FilterRange
                    label={t('FindMoorings.length', 'Length')}
                    min={FILTER_LIMITS.length.min}
                    max={FILTER_LIMITS.length.max}
                    step={FILTER_LIMITS.length.step}
                    values={[filter.length_low, filter.length_high]}
                    onChange={handleRangeChange('length_low', 'length_high')}
                  />
                  <FilterRange
                    label={t('FindMoorings.beam', 'Beam')}
                    min={FILTER_LIMITS.beam.min}
                    max={FILTER_LIMITS.beam.max}
                    step={FILTER_LIMITS.beam.step}
                    values={[filter.beam_low, filter.beam_high]}
                    onChange={handleRangeChange('beam_low', 'beam_high')}
                  />
                  {shouldShowFullFilterInMarina && (
                    <FilterRange
                      label={t('FindMoorings.draft', 'Draft')}
                      min={FILTER_LIMITS.draft.min}
                      max={FILTER_LIMITS.draft.max}
                      step={FILTER_LIMITS.draft.step}
                      values={[filter.draft_low, filter.draft_high]}
                      onChange={handleRangeChange('draft_low', 'draft_high')}
                    />
                  )}
                  {/* Disable for now */}
                  {/* <FilterRange
                                        label={t('FindMoorings.waterLevel', 'Water Level')}
                                        min={FILTER_LIMITS.waterLevel.min}
                                        max={FILTER_LIMITS.waterLevel.max}
                                        step={FILTER_LIMITS.waterLevel.step}
                                        value={filter.draft_deviation}
                                        single
                                        onChange={handleSingleRangeChange('draft_deviation')}
                                    /> */}
                </div>
                {/* {boatTypeOptions.length > 0 && ( */}
                {shouldShowFullFilterInMarina &&
                  false && ( // DIsable boat type filter for now
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: COLORS.MAIN_BLUE,
                          textTransform: 'uppercase',
                          fontSize: 12,
                          letterSpacing: 0.4,
                        }}>
                        {t('FindMoorings.boatTypes', 'Boat types')}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gap: 12,
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(180px, 1fr))',
                        }}>
                        {boatTypeOptions.map((boatType, index) => {
                          const label =
                            boatTypeLabels[index] ||
                            boatType ||
                            t('FindMoorings.boatType', 'Boat type');
                          return (
                            <FilterCheckbox
                              key={`boat_type_${index}`}
                              label={label}
                              checked={selectedBoatTypes.includes(index)}
                              onChange={(checked) =>
                                handleBoatTypeToggle(index, checked)
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
        {hasMarinaParam && showCheckinList && (
          <div
            className='find-moorings-checkin-list-card'
            style={checkinListCardStyle}>
            <div style={checkinListContentWrapperStyle}>
              <div style={checkinListContentStyle}>
                <div
                  className='find-moorings-checkin-list-header'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}>
                  <span>{t('FindMoorings.listView', 'List view')}</span>
                  <button
                    type='button'
                    onClick={toggleCheckinListVisibility}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: COLORS.MAIN_BLUE,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                    }}>
                    {t('global.hide', 'Hide')}
                  </button>
                </div>
                <div className='find-moorings-checkin-list-subheader'>
                  {t(
                    'FindMoorings.checkinListSubheader',
                    'Select your berth from the list.',
                  )}
                </div>
                <div className='find-moorings-checkin-list'>
                  {displayedMoorings.length === 0 ? (
                    <div className='find-moorings-checkin-empty'>
                      {t('FindMoorings.noMooringsFound', 'No moorings found')}
                    </div>
                  ) : (
                    displayedMoorings.map((mooring) => (
                      <button
                        type='button'
                        key={mooring.mooring_id}
                        className='find-moorings-checkin-item'
                        onClick={() => {
                          setCheckinListSelectedMooringId(mooring.mooring_id);
                          handlePinClick(mooring);
                        }}>
                        <span className='find-moorings-checkin-item-title'>
                          {mooring.harbour_name} {mooring.berth_number}
                        </span>
                        <span className='find-moorings-checkin-item-meta'>
                          {`${t('FindMoorings.length', 'Length')}: ${formatDimension(mooring.max_length)}m | ${t('FindMoorings.beam', 'Beam')}: ${formatDimension(mooring.max_beam)}m`}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {(selectedMooring || (isPoiModalOpen && selectedPoi)) && (
        <div
          style={{
            position: 'absolute',
            bottom: isMobileDevice() ? 10 : 120,
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobileDevice() ? '85%' : '90%',
            maxWidth: 420,
            zIndex: 3,
            transition: 'opacity 0.2s ease-in-out',
            display: 'flex',
            justifyContent: 'center',
          }}>
          {selectedMooring ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <img
                style={{
                  width: 80,
                  position: 'absolute',
                  left: -40,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                alt='bp_icon'
                src={PIcon}
              />
              <div
                style={{
                  background: '#003066',
                  borderRadius: 16,
                  padding: '18px 18px 18px 58px',
                  color: 'white',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
                }}>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    marginBottom: 12,
                  }}>
                  {`${selectedMooring.harbour_name} ${selectedMooring.berth_number || ''}`}
                </div>
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  {`${t('FindMoorings.dimensions', 'Dimensions')} ${formatMooringLBT(
                    selectedMooring,
                    t('global.atChartZero', 'at chart zero'),
                  )} m`}
                </div>
                {!(
                  canBookUnavailableMooringInCheckin && isUnavailableState
                ) && (
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 'bold',
                      marginBottom: 8,
                      color: availabilityColor,
                    }}>
                    {availabilityLabel}
                  </div>
                )}
                {hasAvailabilities ? (
                  <>
                    {showAvailabilitySelector && (
                      <Form.Group
                        controlId='selectedAvailabilityRange'
                        style={{ marginBottom: 12 }}>
                        <Form.Label style={{ fontSize: 13, marginBottom: 4 }}>
                          {availableDatesLabel}
                        </Form.Label>
                        <Form.Select
                          value={String(selectedAvailabilityIndex)}
                          onChange={handleAvailabilityChange}
                          style={{ color: COLORS.MAIN_BLUE }}>
                          {processedAvailabilities.map(
                            (availability, index) => {
                              const optionLabel = formatDateRange(
                                availability.from_date,
                                availability.to_date,
                              );
                              return (
                                <option
                                  key={`${availability.from_date}-${availability.to_date}-${index}`}
                                  value={index}>
                                  {optionLabel}
                                  {availability.day_by_day
                                    ? ` (${dayByDayLabel})`
                                    : ''}
                                </option>
                              );
                            },
                          )}
                        </Form.Select>
                      </Form.Group>
                    )}
                    <div
                      style={{
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: 12,
                        padding: 12,
                        display: 'flex',
                        gap: 12,
                        flexWrap: 'wrap',
                        alignItems: 'stretch',
                      }}>
                      <div
                        style={{
                          flex: '1 1 150px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          {dateRangeText}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {dailyPriceText}
                        </div>
                      </div>
                      <div
                        style={{
                          width: 1,
                          background: 'rgba(255, 255, 255, 0.2)',
                          minHeight: '100%',
                        }}
                      />
                      <div
                        style={{
                          flex: '1 1 170px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          {t('FindMoorings.totalPrice', 'Total price')}
                          {excludingTouristTaxes
                            ? ` ${t(
                                'FindMoorings.excludingTouristTaxes',
                                '(excluding tourist taxes)',
                              )}`
                            : ''}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {totalPriceText}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  !isUnavailableState && (
                    <div style={{ fontSize: 14, marginTop: 12 }}>
                      {noAvailabilityMessage} f
                    </div>
                  )
                )}
                {requiresConfirmation && (
                  <div
                    style={{
                      fontSize: 14,
                      color: COLORS.ORANGE_MOORING_UNCONFIRMED,
                      marginTop: 8,
                    }}>
                    {t('FindMoorings.confirmationNeeded', {
                      n: selectedMooring?.auto_confirm_delay,
                    })}
                  </div>
                )}
                {showDayByDayNotice && (
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    {t(
                      'FindMoorings.noAdvanceBooking',
                      'No advance booking possible',
                    )}
                  </div>
                )}
                {canBookAvailability && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      type='button'
                      onClick={handleBookingModalOpen}
                      style={{
                        color: '#00aaff',
                        fontWeight: 'bold',
                        fontSize: 20,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}>
                      {t('BpmsMooringMap.bookNow')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <PoiModal
              show={isPoiModalOpen}
              poi={selectedPoi}
              fromDate={fromDate}
              toDate={toDate}
              onHide={handlePoiModalClose}
            />
          )}
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          right: 60,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          zIndex: 2,
          flexWrap: 'wrap',
        }}>
        <a
          href='https://boatpark.app/'
          target='_blank'
          rel='noreferrer'
          style={{ ...buttonBlockStyle }}>
          <img src={logoSvg} alt='Boatpark' style={{ height: '100%' }} />
        </a>
        {platform !== 'android' && (
          <a
            href='https://apps.apple.com/ch/app/boatpark/id1466777753'
            target='_blank'
            rel='noreferrer'
            style={{ ...buttonBlockStyle }}>
            <img
              src={GetInAppStore}
              alt='App Store'
              style={{ height: '100%' }}
            />
          </a>
        )}
        {platform !== 'ios' && (
          <a
            href='https://play.google.com/store/apps/details?id=com.boatpark&hl=de_CH&gl=US'
            target='_blank'
            rel='noreferrer'
            style={{ ...buttonBlockStyle }}>
            <img
              src={GetInPlayStore}
              alt='Play Store'
              style={{ height: '100%' }}
            />
          </a>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 75,
          right: 10,
          zIndex: 2,
        }}>
        <div
          style={{
            ...buttonBlockStyle,
            opacity: geoLocationAvailable ? 1 : 0.5,
            pointerEvents: geoLocationAvailable ? 'auto' : 'none',
          }}
          onClick={handleCenterOnUser}>
          <img
            src={CenterIcon}
            alt='Center on user'
            style={{ height: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}

function FindMoorings({ t }) {
  const [googleMapsCookiesAccepted, setGoogleMapsCookiesAccepted] = useState(
    () => localStorage.getItem('googleMapsCookiesAccepted') === 'true',
  );

  if (!googleMapsCookiesAccepted) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <ConsentWall
          onAccept={() => {
            setGoogleMapsCookiesAccepted(true);
          }}
        />
      </div>
    );
  }

  return <FindMooringsContent t={t} />;
}

export default withTranslation()(FindMoorings);
